import React from "react";
import DOMPurify from "dompurify";
import { Box, Flex, Text } from "@chakra-ui/react";

// CandidateSummaryPanel renders one titled prose summary of a candidate — the
// panel that sits at the top of the CV tab. Two things can fill that slot, and
// they are NOT the same kind of content:
//
//   - "AI summary" (`html`): resumeSummary, generated from the parsed CV as
//     HTML. Sanitised, then styled by .ai-cv-summary, which targets block
//     children (<p>, <ul>, <li>).
//
//   - "Bullhorn Summary" (`text`): externalDescription, the text Bullhorn holds
//     on the record. Often a CV rather than a blurb — pasted or parsed into the
//     description field upstream — so treat it as candidate material. The API
//     only returns it to a recruiter whose agency covers the profile.
//
//     Verified plain across all ~112k populated rows in production — zero
//     contain markup — so it renders as escaped text, not through
//     dangerouslySetInnerHTML. pre-wrap keeps the rare embedded newlines (124
//     rows have any).
//
// Being CV-length (~4k characters at the median, 14k at p95) it gets a
// fixed-height scroll box so one imported candidate can't push the rest of the
// tab off screen.
function CandidateSummaryPanel({ title, html, text, testId }) {
  if (!html && !text) {
    return null;
  }

  return (
    <Box
      borderRadius="5px"
      flex={1}
      background="gray.800"
      p="20px"
      width="100%"
      data-testid={testId}
    >
      <Flex
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
        mb="4"
      >
        <Text fontSize="md" fontWeight="semibold" lineHeight="6">
          {title}
        </Text>
      </Flex>
      <Flex direction={"column"} mb="4">
        {html ? (
          <div
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
            className="ai-cv-summary"
          ></div>
        ) : (
          <Box
            maxH="320px"
            overflowY="auto"
            whiteSpace="pre-wrap"
            fontSize="md"
            lineHeight="6"
          >
            {text}
          </Box>
        )}
      </Flex>
    </Box>
  );
}

export default CandidateSummaryPanel;
